import type {CreateExtArgs} from '@wix/document-manager-core'
import type {Pointer} from '@wix/document-services-types'
import type {DataModelExtensionAPI} from '../../../dataModel/dataModel'
import type {RelationshipsAPI} from '../../../relationships'
import type {LoggerExtAPI} from '../../../logger'
import {DATA_TYPES} from '../../../../constants/constants'
import {stripHashIfExists} from '../../../../utils/refArrayUtils'
import {ReportableError} from '@wix/document-manager-utils'

const DEVICES = ['desktop', 'mobile'] as const

function removeItemRecursively(createExtArgs: CreateExtArgs, dataPointer: Pointer, forceRemoveRootItem: boolean): void {
    const {dal, extensionAPI} = createExtArgs
    const {dataModel} = extensionAPI as DataModelExtensionAPI
    const {relationships} = extensionAPI as RelationshipsAPI
    const dataItem = dal.get(dataPointer)

    if (!dataItem || dataModel.isPersistentDataNode(dataPointer)) {
        return
    }

    if (relationships.isReferenced(dataPointer) && !forceRemoveRootItem) {
        const {logger} = extensionAPI as LoggerExtAPI
        logger.captureError(
            new ReportableError({
                errorType: 'attemptedRemoveOfReferencedDataError',
                message: `${JSON.stringify(dataPointer)} was removed though it has references to it`,
                extras: {
                    dataItemPointer: dataPointer
                }
            })
        )
        return
    }

    const pointersToProcess = relationships.getOwnedReferredPointers(dataPointer)
    dal.remove(dataPointer)

    for (const pointerToProcess of pointersToProcess) {
        removeItemRecursively(createExtArgs, pointerToProcess, false)
    }
}

function removeItemRecursivelyByType(createExtArgs: CreateExtArgs, dataPointer: Pointer): void {
    if (!createExtArgs.dal.has(dataPointer)) {
        return
    }

    removeItemRecursively(createExtArgs, dataPointer, true)
}

function removePageBackgrounds(createExtArgs: CreateExtArgs, pagePointer: Pointer): void {
    const {dal, pointers} = createExtArgs

    const {id: pageId} = pagePointer
    const dataPointer = pointers.data.getDataItemFromMaster(pageId)
    const pageDataItem = dal.get(dataPointer)

    if (pageDataItem.pageBackgrounds) {
        for (const device of DEVICES) {
            const deviceBackground = pageDataItem.pageBackgrounds[device]
            if (deviceBackground?.ref) {
                const innerDataPointer = pointers.data.getDataItem(stripHashIfExists(deviceBackground.ref), pageId)
                removeItemRecursivelyByType(createExtArgs, innerDataPointer)
            }
        }
    }
}

function removeFixerVersions(createExtArgs: CreateExtArgs, pagePointer: Pointer): void {
    const {extensionAPI} = createExtArgs
    const {dataModel} = extensionAPI as DataModelExtensionAPI

    dataModel.components.removeItemForDesktopAndMobile(pagePointer.id, DATA_TYPES.fixerVersions)
}

export function cleanupPageSpecificData(createExtArgs: CreateExtArgs, pagePointer: Pointer): void {
    removePageBackgrounds(createExtArgs, pagePointer)
    removeFixerVersions(createExtArgs, pagePointer)
}
